import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { CalendarioSemana } from './calendario/CalendarioSemana'
import { CalendarioMes } from './calendario/CalendarioMes'
import { PanelDia } from './calendario/PanelDia'
import { type DiaSemana, DIAS_SEMANA, type SemanaRepartos } from '../hooks/useCalendarioSemana'
import { useCalendarioMes } from '../hooks/useCalendarioMes'
import { colorDeDia } from './agendamiento/coloresDia'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { IconoBuscar } from '../componentes/iconos'

/**
 * Calendario de repartos, con dos vistas: MES (rejilla tipo HEKKIO, por defecto)
 * y SEMANA (B.1). El mes es la vista de referencia pedida: rejilla con píldoras
 * (fondo=ruta, marca=estado) y leyenda; al pulsar un día se abre el panel del
 * día para ver los repartos y añadir uno nuevo (opción "ver + añadir").
 */

// lunes..viernes -> r1..r5; el fin de semana no tiene ruta de reparto (neutro).
const DIA_A_RUTA: Partial<Record<DiaSemana, DiaRuta>> = {
  lunes: 'r1',
  martes: 'r2',
  miercoles: 'r3',
  jueves: 'r4',
  viernes: 'r5',
}

function colorDeDiaSemana(dia: DiaSemana): string {
  const ruta = DIA_A_RUTA[dia]
  return ruta ? colorDeDia(ruta).clase : ''
}

type Vista = 'mes' | 'semana'

export function Calendario() {
  const navigate = useNavigate()
  const [parametros, setParametros] = useSearchParams()
  const [vista, setVista] = useState<Vista>('mes')
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth())
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null)
  // Búsqueda de cliente desde la URL (?cliente=): el botón "En calendario" del
  // Perfil llega con el nombre puesto, y es compartible/bookmarkeable.
  const [busqueda, setBusqueda] = useState(() => parametros.get('cliente') ?? '')

  // Añadir un reparto = ir al agendamiento con ese día ya elegido. Se reutiliza
  // el flujo completo de agendar (resolver + publicar) en vez de duplicarlo.
  const irAAgendar = (iso: string) => navigate(`/agendamiento?fecha=${iso}`)

  const cambiarBusqueda = (v: string) => {
    setBusqueda(v)
    const sig = new URLSearchParams(parametros)
    if (v.trim()) sig.set('cliente', v)
    else sig.delete('cliente')
    setParametros(sig, { replace: true })
  }

  return (
    <section className="flex flex-col gap-4 p-4">
      {/* Toggle mes/semana + buscador de cliente (resalta sus repartos). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1" data-tour="cal-vista">
          <BotonVista activa={vista === 'mes'} onClick={() => setVista('mes')}>Mes</BotonVista>
          <BotonVista activa={vista === 'semana'} onClick={() => setVista('semana')}>Semana</BotonVista>
        </div>
        {vista === 'mes' ? (
          <div className="relative w-full max-w-xs">
            <IconoBuscar aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => cambiarBusqueda(e.target.value)}
              placeholder="Buscar cliente en el calendario"
              aria-label="Buscar cliente en el calendario"
              className="w-full rounded-lg border border-borde-fuerte bg-superficie py-2 pl-9 pr-3 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
            />
          </div>
        ) : null}
      </div>

      {vista === 'mes' ? (
        <VistaMes
          anio={anio}
          mes={mes}
          diaAbierto={diaAbierto}
          resaltar={busqueda.trim()}
          onCambiarMes={(a, m) => { setAnio(a); setMes(m); setDiaAbierto(null) }}
          onSeleccionarDia={setDiaAbierto}
          onCerrarDia={() => setDiaAbierto(null)}
          onAnadir={irAAgendar}
        />
      ) : (
        <VistaSemana onAnadir={irAAgendar} />
      )}
    </section>
  )
}

function BotonVista({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={[
        'rounded-md px-3 py-1.5 text-sm font-medium ring-1 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        activa ? 'bg-acento text-acento-contraste ring-transparent' : 'text-tinta-2 ring-borde hover:bg-superficie',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function VistaMes({
  anio, mes, diaAbierto, resaltar, onCambiarMes, onSeleccionarDia, onCerrarDia, onAnadir,
}: {
  anio: number
  mes: number
  diaAbierto: string | null
  resaltar: string
  onCambiarMes: (anio: number, mes: number) => void
  onSeleccionarDia: (iso: string) => void
  onCerrarDia: () => void
  onAnadir: (iso: string) => void
}) {
  const mesId = `${anio}-${String(mes + 1).padStart(2, '0')}`
  const { data, isPending, isError, error, refetch } = useCalendarioMes(mesId)

  if (isPending) return <Cargando mensaje="Cargando el mes" />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  // ¿Hay algún reparto del cliente buscado en el mes visible? Si no, se avisa
  // (puede tenerlo en otro mes: el usuario navega con las flechas).
  const q = resaltar.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const hayCoincidencia =
    !q ||
    Object.values(data).some((reps) =>
      reps.some((r) => r.cliente.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(q)),
    )

  return (
    <div className="flex flex-col gap-4">
      {resaltar && !hayCoincidencia ? (
        <p className="rounded-lg border border-borde-fuerte bg-elevado px-3 py-2 text-sm text-tinta-2">
          Sin repartos de <span className="font-semibold text-tinta">“{resaltar}”</span> en este mes.
          Prueba otro mes con las flechas.
        </p>
      ) : null}
      <CalendarioMes
        repartosPorFecha={data}
        anio={anio}
        mes={mes}
        resaltar={resaltar}
        onCambiarMes={onCambiarMes}
        onSeleccionarDia={onSeleccionarDia}
      />
      {diaAbierto ? (
        <PanelDia
          iso={diaAbierto}
          repartos={data[diaAbierto] ?? []}
          onAnadir={onAnadir}
          onCerrar={onCerrarDia}
        />
      ) : null}
    </div>
  )
}

function VistaSemana({ onAnadir }: { onAnadir: (iso: string) => void }) {
  // Día cuya vista completa (PanelDia, igual que el calendario mensual) está
  // abierta desde "Ver más" o desde un nombre clicable.
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null)
  // La semana se DERIVA de los repartos del mes (endpoint real que ya funciona),
  // en vez de un endpoint /calendario de semana que no existe (daba semana vacía).
  // Si la semana cruza dos meses, se piden ambos y se combinan.
  const isoPorDia: Record<DiaSemana, string | null> = {
    lunes: isoDeDiaSemana('lunes'),
    martes: isoDeDiaSemana('martes'),
    miercoles: isoDeDiaSemana('miercoles'),
    jueves: isoDeDiaSemana('jueves'),
    viernes: isoDeDiaSemana('viernes'),
    sabado: isoDeDiaSemana('sabado'),
    domingo: isoDeDiaSemana('domingo'),
  }
  // Meses (YYYY-MM) que toca la semana visible.
  const meses = Array.from(
    new Set(Object.values(isoPorDia).filter((v): v is string => !!v).map((iso) => iso.slice(0, 7))),
  )
  const mesA = useCalendarioMes(meses[0] ?? null)
  const mesB = useCalendarioMes(meses[1] ?? null) // null → el hook no consulta

  if (mesA.isPending || (meses[1] && mesB.isPending)) return <Cargando mensaje="Cargando la semana" />
  if (mesA.isError) return <EstadoError error={mesA.error} onReintentar={() => mesA.refetch()} />

  const porFecha = { ...(mesA.data ?? {}), ...(mesB.data ?? {}) }

  // Arma el Record por día de la semana con los repartos reales de cada fecha.
  const repartosPorDia = DIAS_SEMANA.reduce((acc, dia) => {
    const iso = isoPorDia[dia]
    const reps = iso ? porFecha[iso] ?? [] : []
    acc[dia] = reps.map((r) => ({
      id: r.id,
      cliente: r.cliente,
      ruta: r.ruta,
      resumen: r.bono,
    }))
    return acc
  }, {} as SemanaRepartos)

  return (
    <>
      <CalendarioSemana
        repartosPorDia={repartosPorDia}
        colorDeDia={colorDeDiaSemana}
        tituloSemana="Repartos de la semana"
        onSeleccionarDia={(dia) => {
          const iso = isoDeDiaSemana(dia)
          if (iso) onAnadir(iso)
        }}
        // "Ver más" y los nombres clicables abren la MISMA vista del calendario
        // general (PanelDia): todos los repartos del día, con su detalle.
        onVerDia={(dia) => {
          const iso = isoPorDia[dia]
          if (iso) setDiaAbierto(iso)
        }}
        onVerReparto={(dia) => {
          const iso = isoPorDia[dia]
          if (iso) setDiaAbierto(iso)
        }}
      />
      {diaAbierto ? (
        <PanelDia
          iso={diaAbierto}
          repartos={porFecha[diaAbierto] ?? []}
          onAnadir={onAnadir}
          onCerrar={() => setDiaAbierto(null)}
        />
      ) : null}
    </>
  )
}

/**
 * Fecha ISO del día de ruta (lunes..viernes) en la semana en curso. Se calcula
 * a partir de hoy: el lunes de esta semana + el desplazamiento del día.
 */
function isoDeDiaSemana(dia: DiaSemana): string | null {
  const offset: Partial<Record<DiaSemana, number>> = {
    lunes: 0, martes: 1, miercoles: 2, jueves: 3, viernes: 4,
  }
  const desp = offset[dia]
  if (desp === undefined) return null
  const hoy = new Date()
  // getDay(): 0=domingo..6=sábado. Lunes de esta semana:
  const diaSemana = hoy.getDay()
  const restaLunes = diaSemana === 0 ? 6 : diaSemana - 1
  const lunes = new Date(hoy)
  lunes.setDate(hoy.getDate() - restaLunes + desp)
  const y = lunes.getFullYear()
  const m = String(lunes.getMonth() + 1).padStart(2, '0')
  const d = String(lunes.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
